/*
  Provides a network interface shim that is uniform to react-native and standard web.
*/

import $ from 'jquery';
import sleep from 'sleep-promise';
import { getShared, allowMulti, decodeUrlParams, setUrlParams } from './Utils.js';
import { getReloadTimeStr } from '../constants/Meta';
import { elasticLogEvent } from './ElasticLog';
import { getConfig } from '../utils/configUtils';

// import fetch from 'node-fetch'; //only when vod in test lib

const UNAUTHORIZED = 401;

/*
 * Attempts a given network fetch repeatedly until either success or the number of attempts
 * exceeds a specified maximum.  If a failure comes back as rejected for auth reasons, a
 * token refresh is attempted.
 *
 * @param {string} logLabel - for logging purposes to assist log analysis.
 * @param {string} url - the url for the fetch operation.
 * @param {object} options - parameters for the call (method, headers, etc).
 * @param {int} maxAttempts - the maximum number of times to try the call.  If <=0, will be
 *    set to 1.
 * @param {int} delayTicks - the number of ticks to wait between retries.
 */
async function fetchRetry(logLabel, url, options = {}, maxAttempts = 10, delayTicks = 1000) {
  maxAttempts = Math.max(maxAttempts, 1);
  logLabel = logLabel + ':fetchRetry';
  let tokenRefreshIter = undefined; //undefined means haven't done a token refresh; integer N means refreshed on attempt N
  let errors = [];
  let attemptNumber = 0;

  const logErrs = (succeeded) => {
    let elasticLogError = getShared()['elasticLogError'] || (() => {});
    let errsStr = errors.join('|');
    if (succeeded || allowMulti(`${logLabel}-${errsStr}`, 1, 1))
      elasticLogError({
        what: logLabel,
        url,
        succeeded,
        tokenRefreshIter,
        attemptNumber,
        maxAttempts,
        errors: errsStr,
      });
  };

  let errMsg = 'Error placeholder';
  for (; attemptNumber < maxAttempts; ++attemptNumber) {
    try {
      let response = await fetch(url, options);

      if (response.ok) {
        if (errors.length) logErrs(true);
        return response; //success; exit the loop and the function
      }

      errMsg = 'status:' + response.status;
      errors.push(errMsg);
      if (response.status === UNAUTHORIZED && tokenRefreshIter === undefined) {
        let settings = getShared()['settings'];
        if (settings && settings.getEnv()) {
          await settings.getEnv().refreshToken(); //changes value of authToken variable
          options.headers['x-token'] = settings.getAuthToken();
          tokenRefreshIter = attemptNumber;
          --attemptNumber; //don't count this against our retries
        }
        else {
          errors.push(`no ${settings ? 'env' : 'settings'}`);
        }
      }
      else if (400 <= response.status && response.status <= 500) break; //don't retry these errors, it's a losing battle
    }
    catch (error) {
      errMsg = 'caught:' + error.message;
      errors.push('caught:' + error.message);
    }
    await sleep(delayTicks);
  }

  //bottomed out
  logErrs(false);
  throw new Error(`fetchRetry:${logLabel}:${errMsg}`);
}

/*
    Call this with an options record that looks something like this:

    let options = {
      url: 'https://whatever.com:1234/someurl',
      type: 'POST',
      contentType: 'application/json',
      dataType: 'json',      
      data:JSON.stringify({someField:'someData'})
    };

    Results are returned as objects.  When there are errors, the 'error' field
    of the returned object will be populated.  When there is no error, the 'result'
    field will be populated.
*/
async function ajax(options) {
  let url = options.url;

  let fetchOpts = {
    method: options.type,
    body: options.data,
  };

  // if(options.headers)
  //   fetchOpts.headers = new Headers(options.headers);
  fetchOpts.headers = options.headers;

  return new Promise(async (resolve, reject) => {
    try {
      let response = await fetch(url, fetchOpts);
      if (response.ok) {
        resolve({ result: await response.json() });
      }
      else {
        resolve({ error: { status: response.status, msg: `${response.statusText} (${response.status})` } });
      }
    }
    catch (err) {
      resolve({ error: { msg: options.url + ': ' + err } });
    }
  });
}

function reloadNoCache(overrideUrl) {
  //The headers below are the results of fishing on stackoverflow for recipes
  // to invalidate cache client-side.  The headers remain here in the code but
  // note that THEY DO NOT SEEM TO WORK RELIABLY... once in a while they simply
  // fail to force the loading of a newly deployed version.
  // So instead, as our real solution we've gone to passing in a url that points
  // to a copy of the current deploy's index.html, put in place during the build
  // pipeline, named in such a way that it cannot have previously been cached.
  //
  //The recipes (which did not reliably work and which are reflected in the below headers):
  // https://stackoverflow.com/questions/10719505/force-a-reload-of-page-in-chrome-using-javascript-no-cache
  // https://stackoverflow.com/questions/49547/how-do-we-control-web-page-caching-across-all-browsers
  let headers = {
    pragma: 'no-cache',
    Expires: -1,
    'cache-control': 'no-store, must-revalidate, max-age=0',
  };

  let url = overrideUrl || getConfig().loadUrl || window.location.origin;
  let reloadTimeStr = getReloadTimeStr();
  if (reloadTimeStr == 'default')
    url = window.location.href; //provision to allow us to keep reloading a non-top-level incarnation...
  else if (reloadTimeStr) url = window.location.origin + '/' + reloadTimeStr + '.index.html';

  //swizzle session into url.prvsess; consumer is managedDevices ui in mbot
  if(getShared().hallpassSession) {
    let urlParams = decodeUrlParams(url);
    urlParams.prvsess = getShared().hallpassSession;
    url = setUrlParams(url, urlParams);
  }


  elasticLogEvent({ what: 'reload', msg: 'Going to reload with url: ' + url, url });

  $.ajax({ url: url || window.location.href, headers }).done(() => {
    if (url) window.location = url;
    else window.location.reload(true);
  });
}

export { ajax, reloadNoCache, fetchRetry };
